import React, { useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import { css } from 'styled-components';
import { useSearchParams } from 'next/navigation';
import { Button } from '@lmn/design-system';
import ChangeTransportCard from '@/app/components/hotel-detail/changeTransport/ChangeTransportCard';
import type { ChangeTransportCardProps } from '@/app/components/hotel-detail/changeTransport/ChangeTransportCard';
import { useTranslation } from '@/app/components/translations/Translation';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import {
  useTransportIsChangingFilters,
  useTransportPageNumber,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersProvider';
import { useTransportNotAvailable } from '@/app/components/hotel-detail/flight-train/TransportNotAvailableProvider';
import { useCurrentFilterStatus } from '@/app/components/hotel-detail/CurrentFilterStatusProvider';
import useTransportsFetch, {
  fetchTravelTransports,
  isTravelTransportsResponseError,
} from '@/app/components/hotel-detail/travel-api/transportsFetch';
import useUserConfig from '@/app/components/hotel-detail/hooks/useUserConfig';
import { URLParameters } from '@/app/common/urlParameters';
import { useSearchSummary } from '@/app/components/hotel-detail/SearchSummary';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import { getPriceIncrementLabel } from '@/app/components/hotel-detail/changeTransport/priceIncrement.ts';
import { useCreateAsyncPricing } from '../PricingProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { useChangeTransportDetailsOpenModal } from './ChangeTransportDetailsModalProvider';
import { getUniqueSortedTransportClasses } from './ChangeTransportDetailsModal';
import TransportSectionSkeleton from '../flight-train/TransportInfoSkeleton';
import { EvoTransportCardSkeleton } from '../evo/transport/EvoTransportCardSkeleton';
import { useIsDesktop } from '../../search/utils/useSsrMedia';

export default function ChangeTransportList(
  props: React.HTMLAttributes<HTMLElement> & {
    CardComponent?: ComponentType<ChangeTransportCardProps>;
    onDirectSelect?: (transportId: string, transportClass: string) => void;
    evoStyle?: boolean;
  }
) {
  const { isTrain } = useUrlSearchParams();
  const { CardComponent = ChangeTransportCard, onDirectSelect, evoStyle = false, ...rest } = props;

  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        row-gap: 1em;
        position: relative;
      `}
      {...rest}
    >
      <FirstPageTransportList
        isTrain={isTrain}
        CardComponent={CardComponent}
        onDirectSelect={onDirectSelect}
        evoStyle={evoStyle}
      />
      <LoadMoreButton CardComponent={CardComponent} onDirectSelect={onDirectSelect} evoStyle={evoStyle} />
      <LoadingLayer />
    </div>
  );
}

function FirstPageTransportList(props: {
  isTrain: boolean;
  CardComponent: ComponentType<ChangeTransportCardProps>;
  onDirectSelect?: (transportId: string, transportClass: string) => void;
  evoStyle?: boolean;
}) {
  const { isTrain, CardComponent, onDirectSelect, evoStyle = false } = props;
  const { data: travelTransports, error: transportsFetchError } = useTransportsFetch();
  return (
    !transportsFetchError && (
      <TransportList
        list={(travelTransports as FullAvailableTransportResponse)?.availableTransports}
        isTrain={isTrain}
        CardComponent={CardComponent}
        onDirectSelect={onDirectSelect}
        evoStyle={evoStyle}
      />
    )
  );
}

function TransportList(props: {
  list: AvailableTransportEndpointResponse | undefined | null;
  isTrain: boolean;
  CardComponent: ComponentType<ChangeTransportCardProps>;
  onDirectSelect?: (transportId: string, transportClass: string) => void;
  evoStyle?: boolean;
}) {
  const { localization } = usePageWrapperData();

  const { list, isTrain, CardComponent, onDirectSelect, evoStyle = false } = props;
  const { transportNotAvailableIds } = useTransportNotAvailable();
  const { openModal } = useChangeTransportDetailsOpenModal();
  const { toDisplayPrice } = usePriceDisplay();

  const onOpenDetailsClick = (transport: AvailableTransport) => {
    openModal(transport);
  };

  return list ? (
    <>
      {list.map(
        (transport) =>
          transport && (
            <CardComponent
              key={transport.id}
              onOpenDetailsClick={() => onOpenDetailsClick(transport)}
              onSelectClick={
                onDirectSelect
                  ? () =>
                      onDirectSelect(
                        transport.id,
                        getUniqueSortedTransportClasses(transport.outbound, transport.inbound)
                      )
                  : undefined
              }
              price={getPriceIncrementLabel({
                priceIncrement: toDisplayPrice(transport.priceIncrement),
                localization,
              })}
              realPrice={transport.price}
              priceFromAPI={transport.priceFromAPI}
              isTrain={isTrain}
              inbound={transport.inbound}
              outbound={transport.outbound}
              co2Footprint={transport.co2Footprint}
              transportId={transport.id}
              isEnabled={!transportNotAvailableIds.includes(transport.id)}
              numberOfBaggagePiecesIncluded={transport.numberOfBaggagePiecesIncluded ?? undefined}
            />
          )
      )}
    </>
  ) : (
    <TansportListSkeleton evoStyle={evoStyle} />
  );
}

function TansportListSkeleton({ evoStyle = false }: { evoStyle?: boolean }) {
  const { commonData } = usePageWrapperData();
  const isDesktop = useIsDesktop(commonData.isDesktopDevice);

  if (evoStyle) {
    return (
      <div
        css={`
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        {Array.from({ length: 5 }, (_, index) => (
          <EvoTransportCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      <TransportSectionSkeleton isDesktop={isDesktop} />
      <TransportSectionSkeleton isDesktop={isDesktop} />
      <TransportSectionSkeleton isDesktop={isDesktop} />
      <TransportSectionSkeleton isDesktop={isDesktop} />
    </div>
  );
}

export function LoadingLayer() {
  const { isChanging } = useTransportIsChangingFilters();
  const { data: travelTransports } = useTransportsFetch();
  // MPH-469: the overlay + rosa progress bar are meant for reloads of an already-rendered
  // list (filter/flight change). On the initial load the list shows skeletons, so the bar
  // would only peek out from behind the first skeleton card's rounded corner. SWR keeps the
  // previous data during reloads (keepPreviousData), so gating on `data` shows the bar only
  // when there is a list to reload, not during the initial load.
  const showLoading = isChanging && !!travelTransports;

  return (
    <>
      <div
        css={css`
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          background: ${({ theme }) => theme.commons.colors.gray0};
          opacity: ${showLoading ? 0.7 : 0};
          visibility: ${showLoading ? 'visible' : 'hidden'};
          transition: 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
        `}
      />
      <div
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          width: ${showLoading ? '100%' : '0%'};
          height: 4px;
          background-color: ${(p) => p.theme.commons.colors.secondary};
          visibility: ${showLoading ? 'visible' : 'hidden'};
          transition: width 2000ms cubic-bezier(0, 0, 0.58, 1);
        `}
      />
    </>
  );
}

function LoadMoreButton({
  CardComponent,
  onDirectSelect,
  evoStyle = false,
}: {
  CardComponent: ComponentType<ChangeTransportCardProps>;
  onDirectSelect?: (transportId: string, transportClass: string) => void;
  evoStyle?: boolean;
}) {
  const { t } = useTranslation();
  const { pageType, priceType } = useInitialParams();
  const { searchInfoData, localization, commonData } = usePageWrapperData();
  const urlSearchParams = useUrlSearchParams();
  const { data: asyncPricing } = useCreateAsyncPricing();
  const { data: travelTransports, error: transportsFetchError } = useTransportsFetch();
  const { selectedMealPlan } = useSelectedMealPlan();
  const pageConfiguration = (travelTransports as FullAvailableTransportResponse)?.pageConfiguration;
  const {
    page: travelFilterPage,
    setPage: setTravelFilterPage,
    currentFiltersStatus,
    setVisibleTransports,
  } = useCurrentFilterStatus();
  const { page, setPage } = useTransportPageNumber();
  const [extraTransports, setExtraTransports] = useState<AvailableTransportEndpointResponse>([]);
  const { nights } = useSearchSummary({
    useRoomDates: false,
    intlDateFormat: { weekday: 'short', day: 'numeric', month: 'short' },
  });
  const { data: staticDataHotel } = useFetchHotelStatic();
  const guests = urlSearchParams.totalGuestsWithoutInfants;
  const { showBackToTravel } = useUserConfig();
  const searchParams = useSearchParams();
  const debug = searchParams.get(URLParameters.DEBUG) ?? 'false';

  const price = selectedMealPlan?.finalPrice || 0;
  const rateId = selectedMealPlan?.id ?? '';

  useEffect(() => {
    if (travelFilterPage > 1) {
      fetchTravelTransports({
        transportRequest: {
          pricingId: asyncPricing?.pricingId || '',
          price,
          businessProfileId: localization.businessProfile,
          pageType,
          priceType,
          guests,
          nights,
          filters: currentFiltersStatus,
          showBackToTravel,
          isTrain: urlSearchParams.isTrain,
          localization: { lang: localization.lang, locale: localization.locale, country: localization.country },
          hotelCoordinates: staticDataHotel?.coordinates,
        },
        rateId,
        page,
        searchInfoData,
        commonData,
        debug,
        includePreselected: true,
      }).then((response) => {
        if (!isTravelTransportsResponseError(response)) {
          const newTransports = (response as FullAvailableTransportResponse).availableTransports;
          setExtraTransports((prev) => prev.concat(newTransports));
          // Update visible transports in context
          setVisibleTransports((prev) => prev.concat(newTransports));
        }
      });
    } else if (extraTransports.length) {
      setExtraTransports([]);
    }
  }, [travelFilterPage]);

  return (
    <>
      {extraTransports.length && !transportsFetchError ? (
        <TransportList
          list={extraTransports}
          isTrain={urlSearchParams.isTrain}
          CardComponent={CardComponent}
          onDirectSelect={onDirectSelect}
        />
      ) : null}
      {pageConfiguration?.totalPages && page < pageConfiguration.totalPages ? (
        <Button
          onClick={() => {
            setPage((prev) => prev + 1);
            setTravelFilterPage((prev) => prev + 1);
          }}
          variant="primary"
          appearance={evoStyle ? 'outlined' : undefined}
          css={
            evoStyle
              ? css`
                  width: 100%;
                  min-height: 56px;
                `
              : css`
                  width: fit-content;
                  margin: auto;
                  min-height: 48px;
                `
          }
        >
          {urlSearchParams.isTrain
            ? t('vacation.trainavailability.results.show_more')
            : t('vacation.flightavailability.results.show_more')}
        </Button>
      ) : null}
    </>
  );
}
